import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/loyalty-accounts',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/loyalty-accounts',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/loyalty-accounts',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/loyalty-accounts',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/loyalty-accounts',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/loyalty-accounts',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/loyalty-accounts',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/loyalty-accounts',
        destPath: '',
      },
    ],
    '*.pub.wix-code.com': [
      {
        srcPath: '/_api/loyalty-accounts',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/loyalty-accounts',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/loyalty-accounts',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_loyalty_accounts';

/**
 * Creates a loyalty account for a site contact.
 *
 * To create a new loyalty account, a customer must first be a site contact with a contact ID, see the Contacts API.
 * The site must also have an [active loyalty program](https://dev.wix.com/docs/rest/crm/loyalty-program/program/activate-loyalty-program)
 * before loyalty accounts can be created.
 */
export function createAccount(payload: object): RequestOptionsFactory<any> {
  function __createAccount({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.loyalty.account.LoyaltyAccounts.CreateAccount',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'account.createdDate' },
              { path: 'account.updatedDate' },
              { path: 'account.lastActivityDate' },
              { path: 'account.tier.updatedDate' },
              { path: 'account.tier.recalculationDate' },
              { path: 'account.pointsExpiration.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createAccount;
}

/**
 * Adds points to a loyalty account.
 *
 * Only a positive amount can be added using this method. To adjust an account's balance
 * for a negative amount, use Adjust Points.
 *
 * This method allows customers to manually earn points to their loyalty accounts. To use this method you must include an `appId` and an `idempotencyKey`.
 * Any string can be set as the `appId` or `idempotencyKey`. In contrast to when an account earns points through an action taken on your site, then,
 * the `appId` automatically sets to the source app that generates the points. In both cases, the transaction `type` is `"EARN"`.
 */
export function earnPoints(payload: object): RequestOptionsFactory<any> {
  function __earnPoints({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.loyalty.account.LoyaltyAccounts.EarnPoints',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts/{accountId}/earn-points',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'account.createdDate' },
              { path: 'account.updatedDate' },
              { path: 'account.lastActivityDate' },
              { path: 'account.tier.updatedDate' },
              { path: 'account.tier.recalculationDate' },
              { path: 'account.pointsExpiration.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __earnPoints;
}

/**
 * Adjusts the point balance of a loyalty account.
 *
 * You can adjust the balance in two ways:
 * - `balance` allows you to set the total points balance to this new amount. The transaction `type` will return as `"ADJUST"`.
 * - `amount` allows you to adjust the points balance by this amount. This amount can be a positive number to increase the points balance or a negative number to decrease the balance. The transaction `type` will return as `"GIVE"`.
 *
 * An account may not be adjusted to result in a negative balance. If you pass an integer in both the `balance` and the `amount` parameters then the `balance` adjustment takes effect and the
 * `amount` adjustment is ignored.
 */
export function adjustPoints(payload: object): RequestOptionsFactory<any> {
  function __adjustPoints({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.loyalty.account.LoyaltyAccounts.AdjustPoints',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts/{accountId}/adjust-points',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'account.createdDate' },
              { path: 'account.updatedDate' },
              { path: 'account.lastActivityDate' },
              { path: 'account.tier.updatedDate' },
              { path: 'account.tier.recalculationDate' },
              { path: 'account.pointsExpiration.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __adjustPoints;
}

/**
 * Retrieves a loyalty account by ID.
 *
 * You can also retrieve an account using a secondary ID, such as a contact ID or a member ID by calling
 * Get Account by Secondary ID.
 */
export function getAccount(payload: object): RequestOptionsFactory<any> {
  function __getAccount({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.loyalty.account.LoyaltyAccounts.GetAccount',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'account.createdDate' },
              { path: 'account.updatedDate' },
              { path: 'account.lastActivityDate' },
              { path: 'account.tier.updatedDate' },
              { path: 'account.tier.recalculationDate' },
              { path: 'account.pointsExpiration.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getAccount;
}

/**
 * Creates a query to retrieve a list of accounts.
 *
 * The `queryLoyaltyAccounts()` method builds a query to retrieve a list of events and returns a `LoyaltyAccountsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `LoyaltyAccountsQueryBuilder` methods onto the query. `LoyaltyAccountsQueryBuilder` methods enable you to sort, filter, and control the results `queryLoyaltyAccounts()` returns.
 *
 * `queryLoyaltyAccounts()` runs with these `LoyaltyAccountsQueryBuilder` defaults, which you can override:
 *
 * - `skip(0)`
 * - `limit(50)`
 * - `descending("_createdDate")`
 *
 * The methods that are chained to `queryLoyaltyAccounts()` are applied in the order they're called. For example, if you apply `ascending("points.balance")` and then `descending("points.earned")`, the results are sorted first by the balance, and then, if there are multiple results with the same balance, the items are sorted by earned points.
 */
export function queryLoyaltyAccounts(
  payload: object
): RequestOptionsFactory<any> {
  function __queryLoyaltyAccounts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.account.LoyaltyAccounts.QueryLoyaltyAccounts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'loyaltyAccounts.createdDate' },
              { path: 'loyaltyAccounts.updatedDate' },
              { path: 'loyaltyAccounts.lastActivityDate' },
              { path: 'loyaltyAccounts.tier.updatedDate' },
              { path: 'loyaltyAccounts.tier.recalculationDate' },
              { path: 'loyaltyAccounts.pointsExpiration.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryLoyaltyAccounts;
}

/**
 * Retrieves the total amount of points earned, redeemed, and adjusted for the entire loyalty program.
 *
 * The `balance` is the current total of points outstanding, while the `earned`, `adjusted`, and `redeemed` amounts
 * are the all-time accumulated amounts. The totals include the amounts for all loyalty accounts.
 */
export function getProgramTotals(payload: object): RequestOptionsFactory<any> {
  function __getProgramTotals({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.loyalty.account.LoyaltyAccounts.GetProgramTotals',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts/program-totals',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getProgramTotals;
}

/** Retrieves the current member's loyalty account. */
export function getCurrentMemberAccount(
  payload: object
): RequestOptionsFactory<any> {
  function __getCurrentMemberAccount({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.loyalty.account.LoyaltyAccounts.GetCurrentMemberAccount',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts/my-account',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'account.createdDate' },
              { path: 'account.updatedDate' },
              { path: 'account.lastActivityDate' },
              { path: 'account.tier.updatedDate' },
              { path: 'account.tier.recalculationDate' },
              { path: 'account.pointsExpiration.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getCurrentMemberAccount;
}

/**
 * Retrieves the account belonging to the specified contact or member.
 *
 * This method retrieves loyalty accounts using either a customer's contact ID or member ID. You can also retrieve an account
 * using the loyalty account ID with Get Account.
 */
export function getAccountBySecondaryId(
  payload: object
): RequestOptionsFactory<any> {
  function __getAccountBySecondaryId({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.loyalty.account.LoyaltyAccounts.GetAccountBySecondaryId',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts/fetch-by',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'account.createdDate' },
              { path: 'account.updatedDate' },
              { path: 'account.lastActivityDate' },
              { path: 'account.tier.updatedDate' },
              { path: 'account.tier.recalculationDate' },
              { path: 'account.pointsExpiration.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getAccountBySecondaryId;
}

/**
 * Retrieves a list of accounts, given the provided filters.
 *
 * You can retrieve selected loyalty accounts with an array of `contactIds` or retrieve a list of all of a site's loyalty accounts
 * with an empty request parameter. Use the `cursorPaging` parameters to limit how many items load at a time.
 *
 * Deprecated method, please use Query Loyalty Accounts or Search Accounts instead.
 * @deprecated It has been replaced with com.wixpress.loyalty.account.LoyaltyAccounts.QueryLoyaltyAccounts(), and will be removed on 2025-06-30.
 */
export function listAccounts(payload: object): RequestOptionsFactory<any> {
  function __listAccounts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.loyalty.account.LoyaltyAccounts.ListAccounts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'accounts.createdDate' },
              { path: 'accounts.updatedDate' },
              { path: 'accounts.lastActivityDate' },
              { path: 'accounts.tier.updatedDate' },
              { path: 'accounts.tier.recalculationDate' },
              { path: 'accounts.pointsExpiration.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listAccounts;
}

/**
 * Retrieves a list of accounts, given the provided filters and search capabilities.
 * Search is executed on the account's name and email values.
 */
export function searchAccounts(payload: object): RequestOptionsFactory<any> {
  function __searchAccounts({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'search.aggregations.range.buckets.from' },
          { path: 'search.aggregations.range.buckets.to' },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.from',
          },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.to',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.loyalty.account.LoyaltyAccounts.SearchAccounts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts/search',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'accounts.createdDate' },
              { path: 'accounts.updatedDate' },
              { path: 'accounts.lastActivityDate' },
              { path: 'accounts.tier.updatedDate' },
              { path: 'accounts.tier.recalculationDate' },
              { path: 'accounts.pointsExpiration.expirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'aggregationData.results.ranges.results.from' },
              { path: 'aggregationData.results.ranges.results.to' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.from',
              },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.to',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.from',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.to',
              },
              { path: 'aggregationData.results.scalar.value' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.scalar.value',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.scalar.value',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchAccounts;
}

/**
 * Retrieves the count of found accounts, given the provided filters and search capabilities.
 *
 * Use with Search Accounts to retrieve the total amount of loyalty accounts on a site.
 */
export function countAccounts(payload: object): RequestOptionsFactory<any> {
  function __countAccounts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.loyalty.account.LoyaltyAccounts.CountAccounts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts/count',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __countAccounts;
}

/**
 * Updates the points balance of multiple accounts.
 *
 * You can adjust the balance in two ways:
 * - `balance` allows you to set the total points balance to this new amount. The transaction `type` will return as `"ADJUST"`.
 * - `amount` allows you to adjust the points balance by this amount. This amount can be a positive number to increase the points balance or a negative number to decrease the balance. The transaction `type` will return as `"GIVE"`.
 *
 * You can use the job ID from the response to call [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job)
 * to retrieve the job details and metadata.
 */
export function bulkAdjustPoints(payload: object): RequestOptionsFactory<any> {
  function __bulkAdjustPoints({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'search.aggregations.range.buckets.from' },
          { path: 'search.aggregations.range.buckets.to' },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.from',
          },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.to',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.loyalty.v1.account',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.loyalty.account.LoyaltyAccounts.BulkAdjustPoints',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressLoyaltyAccountLoyaltyAccountsUrl({
        protoPath: '/v1/accounts/bulk-adjust',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __bulkAdjustPoints;
}
